import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, AlertTriangle, Scale, RotateCcw } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { AgreementDocument, DEFAULT_FORM } from "@/components/contract/AgreementDocument";

// ---------------------------------------------------------------------------
// Public /agreement — the standalone, editable Pre-Draft Advisory Services
// Agreement template. Persists to localStorage so a half-filled draft survives
// tab switches / print previews. The actual document rendering lives in the
// shared <AgreementDocument /> component (also used by the admin contract
// editor + the family contract viewer).
// ---------------------------------------------------------------------------

const STORAGE_KEY = "omnidraftlab-agreement-draft-v1";

export default function AgreementPage() {
  useDocumentTitle("OmniDraftLab · Advisory Agreement");

  // Hide platform watermark on this page.
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "agreement-hide-emergent";
    style.textContent = `[data-testid="made-with-emergent-badge"], #emergent-watermark { display: none !important; }`;
    document.head.appendChild(style);
    return () => { document.getElementById("agreement-hide-emergent")?.remove(); };
  }, []);

  const [form, setForm] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_FORM, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_FORM;
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setPayment = (i, field, v) =>
    setForm((s) => {
      const next = s.payments.slice();
      next[i] = { ...next[i], [field]: v };
      return { ...s, payments: next };
    });
  const addPayment = () => setForm((s) => ({ ...s, payments: [...s.payments, { date: "", amount: "", note: "" }] }));
  const removePayment = (i) => setForm((s) => ({ ...s, payments: s.payments.filter((_, idx) => idx !== i) }));
  const reset = () => {
    if (!confirm("Reset all fields to the empty template? This cannot be undone.")) return;
    setForm(DEFAULT_FORM);
  };

  return (
    <div className="agreement-root min-h-screen bg-[#f4f1ea]" data-testid="agreement-page">
      {/* Top toolbar — preview only, hidden on print */}
      <div className="ag-no-print sticky top-0 z-40 backdrop-blur-md bg-[#f4f1ea]/85 border-b border-black/10">
        <div className="max-w-[8.6in] mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/pitch" className="inline-flex items-center gap-2 text-black/70 hover:text-black text-sm" data-testid="agreement-back">
            <ArrowLeft className="h-4 w-4" /> Back to pitch
          </Link>
          <div className="flex items-center gap-2 text-black/60 text-xs uppercase tracking-[0.22em]">
            <Scale className="h-3.5 w-3.5 text-rose-600" /> Advisory Agreement · Template
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reset} data-testid="agreement-reset" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-black/70 hover:text-black border border-black/10 text-sm">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
            <button onClick={() => window.print()} data-testid="agreement-print" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#04111A] text-white font-semibold text-sm hover:bg-[#0a1e2c] transition">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Big disclaimer banner — preview only */}
      <div className="ag-no-print max-w-[8.6in] mx-auto px-5 pt-5">
        <div className="rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="agreement-disclaimer-banner">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-[13px] leading-snug text-amber-900">
            <strong>This is a draft template — not legal advice.</strong> Pre-draft advisory relationships in Tennessee intersect with the Uniform Athlete Agents Act (UAAA), NCAA NIL/amateurism rules, and the federal SPARTA statute. Have this document <strong>reviewed and finalized by a Tennessee-licensed sports / contracts attorney</strong> before either party signs. OmniDraftLab provides this template for convenience only and disclaims any responsibility for its legal sufficiency in your specific circumstances.
          </div>
        </div>
      </div>

      <AgreementDocument
        form={form}
        set={set}
        setPayment={setPayment}
        addPayment={addPayment}
        removePayment={removePayment}
        sigMode="template"
      />
    </div>
  );
}
